/**
 * What a blog post is, once `tools/vite-blog.ts` has parsed and validated it.
 *
 * **One library, four sites (2026-09-21).** The markdown lives in `content/blog/` at the
 * repository root, beside `assets/media/` and for the same reason: four copies of a post
 * are four posts that drift. Each app's build takes the posts it is about — see
 * `blogContent({ tags })` — and hands them to the components in this folder.
 *
 * This shape mirrors the plugin's output exactly. It is declared here rather than in an app
 * because four apps read it; it is **not** declared in the plugin because the plugin runs in
 * Node and the apps must not import it.
 */
export interface BlogPost {
  slug: string
  title: string
  image: string
  /** Absent on a post still carrying a placeholder image. */
  imageAlt?: string
  /** Card date badge. Absent on a post with no `published` date. */
  date?: { number: string; txt: string }
  /** Long form, for the article's byline. */
  published?: string
  /** `YYYY-MM-DD`, for `article:published_time` and the sitemap. See `seo/postSeo.ts`. */
  publishedIso?: string
  /** Front-matter override for the search snippet, where the excerpt does not serve. */
  description?: string
  category: string
  excerpt: string
  /** Markdown source. Rendered by `Markdown`. */
  body: string
  tags: string[]
  featured: boolean
}

/**
 * Every post is the Foundation's; there are no bylines.
 *
 * **Not a catalogue key and not a `Text`.** It is the organisation's name, which is written
 * the same way in both languages — see the no-abbreviation rule. It is the author on all
 * four sites, because all four are the Foundation's.
 */
export const BLOG_AUTHOR = 'King of Kings Foundation'

/**
 * The share row under every post, on every site.
 *
 * `href: '#'` on all four: there is no share integration, and a share link is a URL built
 * from the page's own address, which this component does not have and must not guess.
 * They are here rather than in each app's data because the row is identical everywhere and
 * four copies of it is four rows that drift — ground rule 5.
 *
 * **This is the one place on the platform where `'#'` is still correct**, and the reason is
 * that the alternative is worse: the footer's `'#'` links were replaced because they
 * *claimed* to be the privacy policy, where these claim only to be a share control that has
 * not been wired. Wiring them is a one-line change here and nowhere else.
 */
export const BLOG_SHARES: { icon: string; href: string; label?: string }[] = [
  { icon: 'facebook', href: '#', label: 'Facebook' },
  { icon: 'twitter', href: '#', label: 'Twitter' },
  { icon: 'linkedin-big-logo', href: '#', label: 'LinkedIn' },
  { icon: 'instagram', href: '#', label: 'Instagram' },
]
